import { useEffect, useRef, useState } from 'react';
import './projectSettings.css';
import { GlobalsVariables, UNASSIGNED_ROOMTYPE_NAME } from 'src/utils/constants';
import { deepCopy, initController } from 'src/components/Planner/controller';
import { initWifiCalc } from "src/components/Planner/planner/wificalc-dom";
import { initMovsen, toggleMotionSensorsMode } from 'src/components/Planner/planner/movsen';
import {
  isLightingActive,
  startLighting,
  stopLighting
} from 'src/pages/PlannerPage/Planner/dialogs/LightingConfirmDialog/lightingActions';
import { initCanvas, plannerInitGlobals } from 'src/components/Planner/planner/planner-init';
import { useParams } from 'react-router';
import { ts, ts2 } from 'src/utils/translate';
import { getFrontendURL } from 'src/utils/url';
import { deviceProvider } from 'src/components/Planner/planner/DeviceProvider';
import { RootState, store } from 'src/app/store';
import { useGetDevicesQuery } from 'src/api/v2/device-v2-ApiSlice';
import { useGetMyProductsQuery } from 'src/api/v2/user-custom-items-v2-ApiSlice';
import { floorV2ApiSlice, useGetFloorRoomsAndRelatedObjectsQuery } from 'src/api/v2/floor-v2-ApiSlice';
import { floorProvider } from 'src/components/Planner/planner/FloorProvider';
import { invalidatePlannerDerivedData } from 'src/components/Planner/planner/plannerCacheInvalidation';
import { RoomTypes } from 'src/model/api/v2/data-contracts';
import { authTokenApiSlice } from 'src/api/auth-token-ApiSlice';
import {
  CustomItemDrop,
  NoteAddToDevice,
  NoteDrop,
  NoteEdit,
  NoteEditOnDevice
} from 'src/pages/PlannerPage/PlannerPage';
import { ReplaceDeviceDialog } from 'src/pages/PlannerPage/Planner/dialogs/ReplaceDeviceDialog/ReplaceDeviceDialog';
import {
  BulkDeleteDeviceDialog
} from 'src/pages/PlannerPage/Planner/dialogs/BulkDeleteDeviceDialog/BulkDeleteDeviceDialog';
import { AdjustFrameDialog } from 'src/pages/PlannerPage/Planner/dialogs/AdjustFrameDialog/AdjustFrameDialog';
import { FrameActionsDialog } from 'src/pages/PlannerPage/Planner/dialogs/FrameActionsDialog/FrameActionsDialog';
import { PendingFrameDrop } from 'src/pages/PlannerPage/Planner/dialogs/FrameActionsDialog/frameActionsActions';
import { ChangeDesignDialog } from 'src/pages/PlannerPage/Planner/dialogs/ChangeDesignDialog/ChangeDesignDialog';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { plannerUiSlice } from 'src/api/planner-ui-slice';
import { useFeatureFlagQuery } from 'src/api/v1/feature-ApiSlice';
import { showSnackBar, startLoading, stopLoading } from 'src/api/ui-slice';
import {
  LightingConfirmDialog
} from 'src/pages/PlannerPage/Planner/dialogs/LightingConfirmDialog/LightingConfirmDialog';
import {
  adjustCenter,
  isPlannerMetric,
  MEASURE_DEFAULT_MAX_CM,
  MEASURE_DEFAULT_MAX_IN,
  MEASURE_DEFAULT_MIN_CM,
  MEASURE_DEFAULT_MIN_IN,
  setMode
} from 'src/components/Planner/planner/planner';


function buildScaleHelpText(): string {
  return isPlannerMetric()
      ? ts2(
          'scale_define_help_metric',
          'Define a measurement on the floor plan (The value should be between {min}cm-{max}cm)',
          { min: String(MEASURE_DEFAULT_MIN_CM), max: String(MEASURE_DEFAULT_MAX_CM) })
      : ts2(
          'scale_define_help_imperial',
          'Define a measurement on the floor plan (The value should be between {min}inch-{max}inch)',
          { min: String(MEASURE_DEFAULT_MIN_IN), max: String(MEASURE_DEFAULT_MAX_IN) });
}

export function Planner(props: {
  floorId: number,
  userRoleInProject: string,
  floorImageUrl: string,
  roomTypes: RoomTypes[],
  onCustomItemDrop?: (drop: CustomItemDrop) => void,
  onNoteDrop?: (drop: NoteDrop) => void,
  onNoteEdit?: (edit: NoteEdit) => void,
  onNoteEditOnDevice?: (edit: NoteEditOnDevice) => void,
  onNoteAddToDevice?: (add: NoteAddToDevice) => void,
}) {

  const dispatch = useAppDispatch();
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const { data: floorRoomsAndRelatedObjectsData } = useGetFloorRoomsAndRelatedObjectsQuery(
      props.floorId,
      {skip: props.floorId == null}
  )
  const { data: devicesData } = useGetDevicesQuery(projectId);
  const { data: myProductsData } = useGetMyProductsQuery();
  const { data: featureFlag } = useFeatureFlagQuery();

  const onSelectDevice = (
      payload: { productNameId: string | null; productId: number | null; roomId: number | null },
  ) => {
    dispatch(plannerUiSlice.actions.setSelectedDevice({
      productNameId: payload.productNameId,
      productId: payload.productId,
    }));
    dispatch(plannerUiSlice.actions.setSelectedRoom({ id: payload.roomId }));
  };

  const onSelectRoom = (roomId: number) => {
    dispatch(plannerUiSlice.actions.setSelectedRoom({ id: roomId }));
    dispatch(plannerUiSlice.actions.setSelectedDevice({ productNameId: null, productId: null }));
  };

  const onScaleSaved = () => {
    dispatch(plannerUiSlice.actions.setScaleSetupCompletedFloorId(props.floorId));
  };

  const onFullscreenChange = (isFullscreen: boolean) =>
      dispatch(plannerUiSlice.actions.setIsFullscreen(isFullscreen));

  const [replaceDeviceId, setReplaceDeviceId] = useState<number | null>(null);
  const [bulkDeleteDeviceId, setBulkDeleteDeviceId] = useState<number | null>(null);
  const [editFrameSerialId, setEditFrameSerialId] = useState<number | null>(null);
  const [changeDesignDeviceId, setChangeDesignDeviceId] = useState<number | null>(null);
  const [frameDrop, setFrameDrop] = useState<PendingFrameDrop | null>(null);
  const [isCanvasReady, setIsCanvasReady] = useState(false);

  const pendingScaleSetupFloorId = useAppSelector((state) => state.plannerUi.pendingScaleSetupFloorId);
  const isFullscreen = useAppSelector((state) => state.plannerUi.isFullscreen);

  const [showLightingConfirm, setShowLightingConfirm] = useState(false);

  const handleLightingToggle = () => {
    if (isLightingActive()) {
      stopLighting();
    } else {
      setShowLightingConfirm(true);
    }
  };

  const handleLightingConfirm = () => {
    setShowLightingConfirm(false);
    dispatch(startLoading('simple'));
    startLighting()
      .catch((err) =>
        dispatch(showSnackBar({
          text: err?.message ?? ts('p_lightcalc_failed', 'Lighting calculation failed.'),
          severity: 'error',
        })))
      .finally(() => dispatch(stopLoading()));
  };

  const isCanvasInitializedRef = useRef(false);

  useEffect(() => {
    if (floorRoomsAndRelatedObjectsData == null) return;
    if (devicesData == null) return;
    if (myProductsData == null) return;

    deviceProvider.setDevices(devicesData, myProductsData);

    const getFloorsResponse = floorV2ApiSlice.endpoints.getFloors.select({project_id: projectId})(
        store.getState() as RootState);
    floorProvider.setFloors(getFloorsResponse.data!!);

    if (isCanvasInitializedRef.current) return;
    isCanvasInitializedRef.current = true;

    GlobalsVariables.env = authTokenApiSlice.endpoints.getAuthKey.select()(store.getState() as RootState).data!!.yiiEnv;
    initController();
    initWifiCalc();
    initMovsen();
    plannerInitGlobals();

    const roomAndRelatedCopy = deepCopy(floorRoomsAndRelatedObjectsData)

      initCanvas(
          props.floorId,
          () => setIsCanvasReady(true),
          () => {},
          projectId,
          props.userRoleInProject,
          roomAndRelatedCopy,
          props.onCustomItemDrop,
          setReplaceDeviceId,
          setBulkDeleteDeviceId,
          onSelectDevice,
          setEditFrameSerialId,
          setChangeDesignDeviceId,
          setFrameDrop,
          props.onNoteDrop,
          props.onNoteEdit,
          props.onNoteEditOnDevice,
          props.onNoteAddToDevice,
          onSelectRoom,
          onScaleSaved,
          onFullscreenChange,
      )
    }, [floorRoomsAndRelatedObjectsData, devicesData, myProductsData]);

  // the canvas persists edits outside RTK Query, so cached floor/quote data goes stale
  useEffect(() => () => invalidatePlannerDerivedData(), []);

  useEffect(() => {
    if (!isCanvasReady) return;
    if (pendingScaleSetupFloorId !== props.floorId) return;
    setMode.wallSelect();
    dispatch(showSnackBar({ text: buildScaleHelpText(), severity: 'info' }));
    dispatch(plannerUiSlice.actions.setPendingScaleSetupFloorId(null));
  }, [isCanvasReady, pendingScaleSetupFloorId, props.floorId, dispatch]);

  useEffect(() => {
    if (!isCanvasReady) return;
    adjustCenter();
  }, [isFullscreen, isCanvasReady]);

  if (floorRoomsAndRelatedObjectsData == null) {
    return (<>...</>)
  }

  return (
      <>
        <div id="mainContainer" className="maxHeight">

          <img src={`${getFrontendURL()}/images/floor_images/${props.floorImageUrl}`} id="floor_bg"/>
          <div className="planner-loader"></div>
          <div id="svgAndChat" className="maxHeight">
            <div id="planner-buttons">
              <div id="planner-buttons-left-main">
                <div className="planner-buttons-cont" id="planner-buttons-actions">
                  <div title={ts('api.floorView;Set proportions','Set proportions')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-wallselect-btn"
                       style={{display: 'none'}}><i style={{transform: 'rotate(45deg)'}}
                                                    className="fa fa-arrows-h"
                                                    aria-hidden="true"></i><span
                      className="planner-hotkey">1</span></div>

                  <div title={ts('api.floorView;Draw outer wall','Draw outer wall')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-drawouter-btn">
                    <img src={`${getFrontendURL()}/images/outer.ico`}/><span className="planner-hotkey">1</span>
                  </div>
                  <div title={ts('api.floorView;Draw','Draw')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-drawing-btn">
                    <img src={`${getFrontendURL()}/images/cur_3.png`}/><span className="planner-hotkey">2</span>
                  </div>
                  <div title={ts('api.floorView;Draw door','Draw door')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-doorselect-btn">
                    <img src={`${getFrontendURL()}/images/door.png`}/><span className="planner-hotkey">3</span>
                  </div>
                  <div title={ts('api.floorView;Draw window','Draw window')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn"
                       id="toggle-windowselect-btn">
                    <img src={`${getFrontendURL()}/images/window.png`}/><span className="planner-hotkey">4</span>
                  </div>
                  <div title={ts('api.floorView;Measure distance between objects','Measure distance between objects')}
                       data-toggle="tooltip" data-placement="bottom" className="planner-icon-btn"
                       id="measure-distance-btn"><img src={`${getFrontendURL()}/images/ruler.png`}/><span
                      className="planner-hotkey">5</span></div>
                  <div title={ts("api.floorView;Set building's materials", "Set building's materials")} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="set-wall-types-btn"><img
                      src={`${getFrontendURL()}/images/wall_type.png`}/><span className="planner-hotkey">6</span>
                  </div>

                  <div title={ts('api.floorView;uPlan Wizard','uPlan Wizard')} data-toggle="tooltip"
                       data-placement="top" className="planner-icon-btn" id="wizard-btn-dropdown"><i
                      className="fa fa-magic" style={{color: "black"}} aria-hidden="true"></i><span
                      className="planner-button-bottom-center"><i
                      className="fa fa-angle-down planner-button-down-arrow"></i></span>
                    <div id="wizard-dropdown-list">
                      <div id="wiz-drop-item-smart" className="wiz-drop-card"><img
                          src={`${getFrontendURL()}/images/wizard_icons/smarthome.png`}/>
                        <span>{ts('api.floorView;Loxone','Loxone')}</span>
                        <i id="lox-drop-arrow" className="fa fa-chevron-down"></i>
                      </div>
                      <div id="wiz-drop-subs-smart" className="wiz-drop-subs-container hidden">
                        <div data-bundle="smarthome" data-config="lighting"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/switched_light.png`}/>
                          <span>{ts('api.floorView;Lighting','Lighting')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="shading"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/shading.png`}/>
                          <span>{ts('api.floorView;Shading','Shading')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="heating"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/planobject_images/Radiator.png`}/>
                          <span>{ts('api.floorView;Heating','Heating')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="music"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/planobject_images/speaker_mounted_in_fake_ceiling.png`}/>
                          <span>{ts('api.floorView;Music','Music')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="controls"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/planobject_images/touchtreefeher.png`}/>
                          <span>{ts('api.floorView;Controls','Controls')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="security"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/planobject_images/siren_tree.png`}/>
                          <span>{ts('api.floorView;Security','Security')}</span>
                        </div>
                        <div data-bundle="smarthome" data-config="ventilator"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/leaf.png`}/>
                          <span>{ts('api.floorView;Ventilation','Ventilation')}</span>
                        </div>
                        <div data-bundle="aio" id="wizard-loxone-all-btn"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/smarthome.png`}/>
                          <span style={{fontWeight: 600}}>{ts('api.floorView;All of the above','All of the above')}</span>
                        </div>
                      </div>
                      <div id="wiz-drop-item-conventional" className="wiz-drop-card">
                        <img src={`${getFrontendURL()}/images/wizard_icons/conventional_plug.png`}/>
                        <span>{ts('api.floorView;Conventional','Conventional')}</span>
                        <i id="conv-drop-arrow" className="fa fa-chevron-down"></i>
                      </div>
                      <div id="wiz-drop-subs-conventional" className="wiz-drop-subs-container hidden">
                        <div data-bundle="conventional" data-config="lighting"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/switched_light.png`}/>
                          <span>{ts('api.floorView;Lighting','Lighting')}</span>
                        </div>
                        <div data-bundle="conventional" data-config="shading"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/shading.png`}/>
                          <span>{ts('api.floorView;Shading','Shading')}</span>
                        </div>
                        <div data-bundle="conventional" data-config="sockets"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/conventional.png`}/>
                          <span>{ts('api.floorView;Sockets','Sockets')}</span>
                        </div>
                        <div data-bundle="conventional" id="wizard-conventional-all-btn"
                             className="wiz-drop-card wiz-drop-subcateg"><img
                            src={`${getFrontendURL()}/images/wizard_icons/conventional_plug.png`}/><span
                            style={{fontWeight: 600}}>{ts('api.floorView;All of the above','All of the above')}</span>
                        </div>
                      </div>
                      <div id="wiz-drop-item-it" className="wiz-drop-card"><img
                          src={`${getFrontendURL()}/images/wizard_icons/it.png`}/><span>{ts('api.floorView;IT System',
                          'IT System')}</span>
                      </div>
                      <div className="wiz-drop-card wiz-drop-card-nohover"
                           style={{
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             fontWeight: 600,
                             border: 'none'
                           }}>
                        <div>{ts('app.frontend.projectSettings;Comfort Level','Comfort Level')}</div>
                        <div id="planner-comfort-stars" data-value="0">
                          <i className="fa fa-star-o" data-value="1"></i>
                          <i className="fa fa-star-o" data-value="2"></i>
                          <i className="fa fa-star-o" data-value="3"></i>
                        </div>
                        <a href="https://www.uplan.io/dincomfortlevels" target="_blank"><i
                            className="fa fa-info-circle"
                            style={{color: "black", marginLeft: "5px"}}></i></a>
                      </div>
                    </div>
                  </div>

                  <div title={ts('api.floorView;uPlan Wizard (Loxone Smart Home)','uPlan Wizard (Loxone Smart Home)')}
                       data-toggle="tooltip" data-placement="bottom"
                       className="planner-icon-btn wizard-hidden-btn hidden"
                       id="wizard-btn-smarthome"><img src={`${getFrontendURL()}/images/wizard_icons/smarthome.png`}/>
                    <span className="planner-hotkey-extra">
                                      <i className="fa fa-cog rotate-cog hidden"></i></span>
                  </div>
                  <div title={ts('api.floorView;uPlan Wizard (Ventilation)','uPlan Wizard (Ventilation)')}
                       data-toggle="tooltip" data-placement="bottom"
                       className="planner-icon-btn wizard-hidden-btn hidden"
                       id="wizard-btn-leaf"><img src={`${getFrontendURL()}/images/wizard_icons/leaf.png`}/>
                    <span className="planner-hotkey-extra"><i
                        className="fa fa-cog rotate-cog hidden"></i></span>
                  </div>
                  <div title={ts('api.floorView;uPlan Wizard (Conventional Electrical System)','uPlan Wizard (Conventional Electrical System)')}
                       data-toggle="tooltip" data-placement="bottom"
                       className="planner-icon-btn wizard-hidden-btn hidden"
                       id="wizard-btn-conventional"><img
                      src={`${getFrontendURL()}/images/wizard_icons/conventional_plug.png`}/>
                    <span className="planner-hotkey-extra"><i
                        className="fa fa-cog rotate-cog hidden"></i></span>
                  </div>
                  <div title={ts('api.floorView;uPlan Wizard (IT System)','uPlan Wizard (IT System)')}
                       data-toggle="tooltip" data-placement="bottom"
                       className="planner-icon-btn wizard-hidden-btn hidden"
                       id="wizard-btn-it"><img src={`${getFrontendURL()}/images/wizard_icons/it.png`}/>
                    <span className="planner-hotkey-extra"><i
                        className="fa fa-cog rotate-cog hidden"></i></span>
                  </div>
                  <div title={ts('api.floorView;uPlan Wizard (All-In-One)','uPlan Wizard (All-In-One)')}
                       data-toggle="tooltip" data-placement="bottom"
                       className="planner-icon-btn wizard-hidden-btn hidden"
                       id="wizard-btn-aio">
                    <img src={`${getFrontendURL()}/images/wizard_icons/aio4.png`}/>
                    <span className="planner-hotkey-extra"><i
                        className="fa fa-cog rotate-cog hidden"></i></span>
                  </div>
                  <div className="p-action-icon planner-icon-separator"></div>
                  <div className="p-action-icon planner-icon-btn" title={ts('api.floorView;Undo','Undo')}
                       data-toggle="tooltip" data-placement="bottom" id="planner-undo-btn"><i
                      style={{color: 'black'}} className="fa fa-undo" aria-hidden="true"></i></div>
                  <div className="p-action-icon planner-icon-btn" title={ts('api.floorView;Redo','Redo')}
                       data-toggle="tooltip" data-placement="bottom" id="planner-redo-btn"><i
                      style={{color: 'black'}} className="fa fa-repeat" aria-hidden="true"></i></div>
                  <div className="p-action-icon planner-icon-separator"></div>
                </div>
                <div className="planner-buttons-cont" id="planner-buttons-visual-adjust">
                  <div title={ts('api.floorView;Toggle visibility settings',' Toggle visibility settings')}
                       data-toggle="tooltip" data-placement="bottom" id="planner-visual-toggler"
                       className="planner-icon-btn planner-icon-btn-sml"><i
                      className="fa fa-caret-right"></i></div>
                </div>
                <div className="planner-buttons-cont planner-buttons-cont-out"
                     style={{position: 'absolute'}} id="planner-buttons-visual">
                  <div title={ts('api.floorView;Toggle opacity of rooms','Toggle opacity of rooms')}
                       data-toggle="tooltip" data-placement="bottom" className="planner-icon-btn"
                       id="toggle-opacity-btn">
                    <i className="fa fa-eye" aria-hidden="true"></i>
                    <span className="planner-hotkey">o</span>
                    <span id="opacity-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Toggle Icon Size','Toggle Icon Size')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" style={{display: 'none'}}
                       id="planner-devsize-s"><img src={`${getFrontendURL()}/images/icon_smallsize_new.svg`}/></div>
                  <div title={ts('api.floorView;Toggle Icon Size','Toggle Icon Size')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="planner-devsize-m">
                    <img
                        src={`${getFrontendURL()}/images/icon_mediumsize_new.svg`}/>
                  </div>
                  <div title={ts('api.floorView;Toggle Icon Size','Toggle Icon Size')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" style={{display: 'none'}}
                       id="planner-devsize-l"><img src={`${getFrontendURL()}/images/icon_largesize_new.svg`}/></div>
                  <div title={ts('api.floorView;Toggle Icon Size','Toggle Icon Size')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" style={{display: 'none'}}
                       id="planner-devsize-xl"><img src={`${getFrontendURL()}/images/icon_xlargesize_new.svg`}/></div>
                  <div title={ts('api.floorView;Toggle showing measurements','Toggle showing measurements')}
                       data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="measure-toggle-btn">
                    <img src={`${getFrontendURL()}/images/ruler.png`}/><span className="planner-hotkey">d</span><span
                      id="measure-display" className="planner-hotkey-extra"></span></div>
                  <div title={ts('api.floorView;Toggle visibility of room names','Toggle visibility of room names')}
                       data-toggle="tooltip" data-placement="bottom" className="planner-icon-btn"
                       id="toggle-plannames-btn">
                    <span style={{transition: "1s ease"}} className="planner-icon-character">A</span>
                    <span className="planner-hotkey">a</span>
                    <span id="roomname-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Toggle device symbols','Toggle device symbols')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-symbols-btn">
                    <img style={{transform: "scale(0.94)", objectFit: "contain"}}
                         src={`${getFrontendURL()}/images/tag-icon-png-1.jpg`}/>
                    <span className="planner-hotkey">e</span>
                    <span id="symbolname-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Toggle notes','Toggle notes')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="toggle-notes-btn">
                    <i className="fa fa-sticky-note-o" style={{color: 'black'}}
                       aria-hidden="true"></i>
                    <span className="planner-hotkey">n</span>
                    <span id="note-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Show motion sensor radiuses','Show motion sensor radiuses')}
                       data-toggle="tooltip"
                       data-placement="bottom"
                       onClick={() => toggleMotionSensorsMode('movementSensor')}
                       className="planner-icon-btn" id="toggle-movsen-btn">
                    <img style={{
                      transition: '1s ease',
                      transform: 'scale(0.8)rotate(90deg)',
                      borderRadius: '10px!important'
                    }}
                         src={`${getFrontendURL()}/images/radius.png`}/>
                    <span className="planner-hotkey">m</span>
                    <span id="motionradius-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Show field of view of cameras','Show field of view of cameras')}
                       data-toggle="tooltip"
                       data-placement="bottom" onClick={() => toggleMotionSensorsMode('camera')}
                       className="planner-icon-btn" id="toggle-movsen-btn"><img
                      src={`${getFrontendURL()}/images/kamera.png`}/><span
                      className="planner-hotkey">k</span><span id="cameraradius-display"
                                                               className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Show WiFi coverage','Show WiFi coverage')} data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn" id="wificalc-btn">
                    <i style={{transition: '1s ease'}} className="fa fa-wifi"
                       aria-hidden="true"></i>
                    <span className="planner-hotkey">w</span>
                    <span id="wifiradius-display" className="planner-hotkey-extra"></span>
                  </div>
                  <div title={ts('api.floorView;Show building materials','Show building materials')}
                       data-toggle="tooltip"
                       data-placement="bottom" className="planner-icon-btn"
                       id="show-build-mat-btn">
                    <img src={`${getFrontendURL()}/images/wall_type.png`}/>
                    <span className="planner-hotkey">q</span>
                    <span id="building-mat-display" className="planner-hotkey-extra"></span>
                  </div>
                  {featureFlag?.test_features_enabled && (
                    <div title={ts('api.floorView;Show lighting calculation','Show lighting calculation')}
                         data-toggle="tooltip"
                         data-placement="bottom" className="planner-icon-btn TEST_FEATURE"
                         onClick={handleLightingToggle}
                         id="toggle-lighting-btn">
                      <i className="fa fa-lightbulb-o"></i>
                      <span className="planner-hotkey">l</span>
                      <span id="lighting-display" className="planner-hotkey-extra"></span>
                    </div>
                  )}

                  <div title={ts('api.floorView;Toggle grid','Toggle grid')}
                       data-toggle="tooltip"
                       data-placement="bottom"
                       className="planner-icon-btn"
                       id="toggle-grid-btn">
                    <i className="fa fa-hashtag"></i>
                    <span className="planner-hotkey">g</span>
                    <span id="show-grid" className="planner-hotkey-extra"></span>
                  </div>
                </div>
              </div>
              {/*End of planner buttons */}
              {/* Start of floor modify*/}
              <div className="planner-buttons-cont planner-modify-item hidden" id="planner-buttons-mod">
                <div className="planner-text-input planner-floormod-input planner-modify-item hidden">
                  <input type="text" id="name-modify-input" className="planner-tooltipped-input"
                         data-toggle="tooltip" data-placement="bottom"
                         title={ts('api.floorView;Place name','Place name')}
                         placeholder={ts('api.floorView;Place name','Place name')}/>
                </div>

                <div className="planner-select-input planner-floormod-input planner-modify-item hidden">
                  <select id="type-modify-input" className="planner-tooltipped-input"
                          data-toggle="tooltip" data-placement="bottom"
                          title={ts('api.floorView;Place type','Place type')}>
                    {props.roomTypes
                    .filter((roomType) => roomType.room_type_name !== UNASSIGNED_ROOMTYPE_NAME)
                    .map((roomType) => (
                        <option value={roomType.id}>{roomType.room_type_name}</option>
                    ))}
                  </select>
                </div>

                <div className="planner-text-input planner-floormod-input planner-modify-item hidden">
                  <input type="text" id="height-modify-input" className="planner-tooltipped-input"
                         data-toggle="tooltip" data-placement="bottom"
                         title={ts('api.floorView;Headroom','Headroom')}/>
                </div>

                <div title={ts('api.floorView;Confirm','Confirm')} data-toggle="tooltip"
                     data-placement="bottom"
                     className="planner-icon-btn planner-modify-item planner-grouping-item hidden"
                     id="confirm-modify-btn">
                  <i style={{color: '#47c260'}} className="fa fa-check" aria-hidden="true"></i>
                  <span className="planner-hotkey planner-hotkey-small">enter</span>
                </div>

                <div title={ts('api.floorView;Delete','Delete')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn planner-modify-item hidden"
                     id="delete-modify-btn">
                  <i style={{color: '#abb5ba'}} className="fa fa-trash" aria-hidden="true"></i>
                </div>

                <div title={ts('api.floorView;Cancel','Cancel')} data-toggle="tooltip"
                     data-placement="bottom"
                     className="planner-icon-btn planner-modify-item planner-grouping-item hidden"
                     id="undo-modify-btn">
                  <i style={{color: '#abb5ba'}} className="fa fa-times" aria-hidden="true"></i>
                  <span className="planner-hotkey planner-hotkey-small">esc</span>
                </div>
              </div>

              {/* End of floor modify*/}
              {/* Start of crop buttons*/}
              <div className="planner-buttons-cont hidden" id="planner-buttons-crop">
                <div className="planner-icon-txt" id="crop-rot-info">{ts('api.floorView;Rotation',
                    'Rotation')}: <b><span className="crop-val" id="crop-rot-val"></span></b><span
                    className="crop-help">{ts('api.floorView;shift+wheel','shift+wheel')}</span></div>
                <div title={ts('api.floorView;Rotate Counterclockwise','Rotate Counterclockwise')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-rot-ccw"><img
                    src={`${getFrontendURL()}/images/rot-ccw.png`}/></div>
                <div title={ts('api.floorView;Rotate Clockwise','Rotate Clockwise')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-rot-cw"><img
                    src={`${getFrontendURL()}/images/rot-cw.png`}/></div>
                <div className="planner-icon-txt" id="crop-zoom-info">{ts('api.floorView;Zoom',
                    'Zoom')}: <b><span className="crop-val" id="crop-zoom-val"></span></b><span
                    className="crop-help">{ts('api.floorView;ctrl+wheel','ctrl+wheel')}</span></div>
                <div title={ts('api.floorView;Zoom in','Zoom in')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-zoom-in"><i
                    className="fa fa-search-plus" aria-hidden="true"></i></div>
                <div title={ts('api.floorView;Zoom out','Zoom out')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-zoom-out"><i
                    className="fa fa-search-minus" aria-hidden="true"></i></div>
                <div title={ts('api.floorView;Reset image','Reset image')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-reset-all"><img
                    src={`${getFrontendURL()}/images/reset.png`}/></div>
                <div title={ts('api.floorView;Confirm changes','Confirm changes')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-confirm"><i
                    style={{color: ' #47c260'}} className="fa fa-check" aria-hidden="true"></i></div>
                <div title={ts('api.floorView;Cancel changes','Cancel changes')} data-toggle="tooltip"
                     data-placement="bottom" className="planner-icon-btn" id="crop-cancel"><i
                    style={{color: '#abb5ba'}} className="fa fa-times" aria-hidden="true"></i></div>
              </div>

              {/* End of crop buttons*/}
              {/* Start of floor buttons*/}
              <div className="planner-buttons-cont planner-icon-firstright"
                   id="planner-buttons-rightside">
              </div>
              {/* End of floor buttons*/}
            </div>
            <div id="planner-sizers">
              <div data-toggle="tooltip" data-placement="top" className="planner-icon-btn"
                   id="zen-mode-on-btn">
                <i style={{color: 'black'}} className="fa" aria-hidden="true"></i>
              </div>
              <div data-toggle="tooltip" data-placement="top" className="planner-icon-btn"
                   id="zen-mode-off-btn">
                <i style={{color: 'black'}} className="fa" aria-hidden="true"></i>
              </div>
              <div id="size-slider"></div>
              <div id="reset-center-btn" className="planner-icon-btn hidden"><i
                  style={{color: 'black'}}
                  className="fa fa-dot-circle-o"
                  aria-hidden="true"></i>
              </div>
              <div title="{ts('api.floorView;Highlight next incorrect device','Highlight next incorrect device')}"
                   data-toggle="tooltip" data-placement="top" id="show-next-bad-btn"
                   className="planner-icon-btn hidden"><i style={{color: 'black'}}
                                                          className="fa fa-step-forward"
                                                          aria-hidden="true"></i></div>
            </div>
            <div id="planner-overlay">
              <div id="planner-overlay-inner">
                <div id="planner-overlay-bg"></div>
                <div id="planner-overlay-content">
                  <img src={`${getFrontendURL()}/images/drag-and-drop.svg`}/>
                  <span>{ts('api.floorView;drag and drop devices from the menu','drag and drop devices from the menu')}</span>
                </div>
              </div>
            </div>

            <div id="rooms"></div>

            <div id="signal-strength" style={{zIndex: 10000, display: 'none'}}>
              <span id="signal-strength-value"></span>
            </div>
            <div className="signal-legend" style={{zIndex: 1000}}>
              <div>
                <div className="signal-legend-grad">
                </div>
              </div>
              <div>
                <svg width="20" height="150" viewBox="0 0 20 150">
                  <line x1="0" y1="1" x2="10" y2="1" stroke="black" stroke-width="1px"></line>
                  <line x1="0" y1="50" x2="10" y2="50" stroke="black" stroke-width="1px"></line>
                  <line x1="0" y1="100" x2="10" y2="100" stroke="black" stroke-width="1px"></line>
                  <line x1="0" y1="149" x2="10" y2="149" stroke="black" stroke-width="1px"></line>

                  <line x1="10" y1="1" x2="10" y2="149" stroke="black" stroke-width="1px"></line>

                  <line x1="10" y1="25" x2="20" y2="25" stroke="black" stroke-width="1px"></line>
                  <line x1="10" y1="75" x2="20" y2="75" stroke="black" stroke-width="1px"></line>
                  <line x1="10" y1="125" x2="20" y2="125" stroke="black" stroke-width="1px"></line>
                </svg>
              </div>
              <div className="signal-legend-icon-cont">
                <div>
                  <img src={`${getFrontendURL()}/images/net_3.png`}/>
                </div>
                <div>
                  <img src={`${getFrontendURL()}/images/net_2.png`}/>
                </div>
                <div>
                  <img src={`${getFrontendURL()}/images/net_1.png`}/>
                </div>
              </div>
            </div>
          </div>
        </div>
        {
          /* needs bootstrap css to display, but bootstrap css causes the design to fall apart */
          /*<AddModNoteModal/>*/}

        {replaceDeviceId != null && (
            <ReplaceDeviceDialog
                onClose={() => setReplaceDeviceId(null)}
                deviceId={replaceDeviceId}
                categories={devicesData?.categories ?? []}
                myProducts={myProductsData?.products ?? []}
            />
        )}
        {bulkDeleteDeviceId != null && (
            <BulkDeleteDeviceDialog
                onClose={() => setBulkDeleteDeviceId(null)}
                deviceId={bulkDeleteDeviceId}
            />
        )}
        {editFrameSerialId != null && (
            <AdjustFrameDialog
                onClose={() => setEditFrameSerialId(null)}
                serialId={editFrameSerialId}
            />
        )}
        {changeDesignDeviceId != null && (
            <ChangeDesignDialog
                onClose={() => setChangeDesignDeviceId(null)}
                deviceId={changeDesignDeviceId}
            />
        )}
        {frameDrop && (
            <FrameActionsDialog frameDrop={frameDrop} onClose={() => setFrameDrop(null)} />
        )}
        <LightingConfirmDialog
            open={showLightingConfirm}
            onConfirm={handleLightingConfirm}
            onCancel={() => setShowLightingConfirm(false)}
        />
      </>
  )
}
